/**
 * Pure mathematical typographic heart rendering engine.
 * Solves the implicit heart curve (x^2 + y^2 - 1)^3 - x^2 * y^3 <= 0
 * to generate horizontal phrase layouts across responsive viewports.
 */

export interface HeartPhraseSource {
  id: string;
  text: string;
  language?: string;
  type: 'love' | 'nickname';
}

export interface RenderOptions {
  width: number;
  height: number;
  loveRatio?: number; // e.g. 70
  nicknameRatio?: number; // e.g. 30
  minFontSize?: number; // e.g. 11
  maxFontSize?: number; // e.g. 22
  textDensity?: 'compact' | 'balanced' | 'spacious';
  animationStyle?: 'none' | 'subtle' | 'gentle';
  colorPalette?: string[];
  highlightFrequency?: number; // e.g. 0.15
  seed?: number;
}

export interface PlacedPhrase {
  id: string;
  text: string;
  language?: string;
  type: 'love' | 'nickname';
  x: number; // left in px
  y: number; // top in px
  width: number; // measured width in px
  height: number; // row height in px
  fontSize: number;
  color: string;
  isHighlight: boolean;
  row: number;
  col: number;
  depth: number; // 0 (outermost outline) to 1 (innermost core)
  phaseDelay: number; // animation delay in milliseconds
}

export interface HeartLayoutResult {
  phrases: PlacedPhrase[];
  totalPhrases: number;
  loveCount: number;
  nicknameCount: number;
  actualLoveRatio: number; // in percentage
  actualNicknameRatio: number; // in percentage
  bounds: { minX: number; maxX: number; minY: number; maxY: number };
}

const DEFAULT_PALETTE = [
  '#5C0011',
  '#800020',
  '#A00030',
  '#C11236',
  '#E32636',
  '#FF334F',
  '#FF667A',
];

/**
 * Parametric evaluation of the classic Cardioid heart curve:
 * x(t) = 16 sin^3(t)
 * y(t) = 13 cos(t) - 5 cos(2t) - 2 cos(3t) - cos(4t)
 * This formulation produces a natural, harmonious, classic heart silhouette
 * with balanced rounded lobes, a gentle proportional cleft, and an organic taper.
 */
function rawHeartY(t: number): number {
  return 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);
}

function rawHeartX(t: number): number {
  return 16 * Math.pow(Math.sin(t), 3);
}

const T_PEAK = 0.98175; // Parameter where dy/dt = 0 (top lobe peak)
const RAW_BOTTOM_Y = -17.0;
const RAW_PEAK_Y = rawHeartY(T_PEAK); // ≈ 11.834
const RAW_CLEFT_Y = 5.0; // Central cleavage height

// Normalization factors to Cartesian coordinates: y in [-1.0, 1.22], x in [-1.12, 1.12]
const SCALE_Y = (1.22 - (-1.0)) / (RAW_PEAK_Y - RAW_BOTTOM_Y);
const OFFSET_Y = -1.0 - SCALE_Y * RAW_BOTTOM_Y;
const SCALE_X = 1.12 / 16.0;

function normToRawY(yn: number): number {
  return (yn - OFFSET_Y) / SCALE_Y;
}

/**
 * Calculates horizontal valid intervals [xStart, xEnd] for a given Cartesian y.
 * Slices through the balanced lobes and tapered heart body.
 */
export function getHeartSlicesAtY(yNorm: number): Array<[number, number]> {
  if (yNorm > 1.22 || yNorm < -1.0) return [];
  const yr = normToRawY(yNorm);
  if (yr > RAW_PEAK_Y || yr < RAW_BOTTOM_Y) return [];

  if (yr > RAW_CLEFT_Y) {
    // Upper lobes: two distinct slices separated by a balanced cleft
    let l = 0, h = T_PEAK;
    for (let i = 0; i < 20; i++) {
      const m = (l + h) / 2;
      if (rawHeartY(m) < yr) l = m; else h = m;
    }
    const t1 = (l + h) / 2;
    const xInner = rawHeartX(t1) * SCALE_X;

    l = T_PEAK; h = Math.PI;
    for (let i = 0; i < 20; i++) {
      const m = (l + h) / 2;
      if (rawHeartY(m) > yr) l = m; else h = m;
    }
    const t2 = (l + h) / 2;
    const xOuter = rawHeartX(t2) * SCALE_X;

    if (xOuter <= xInner) return [];
    return [
      [-xOuter, -xInner],
      [xInner, xOuter],
    ];
  } else {
    // Single continuous horizontal slice through the heart body
    let l = T_PEAK, h = Math.PI;
    for (let i = 0; i < 20; i++) {
      const m = (l + h) / 2;
      if (rawHeartY(m) > yr) l = m; else h = m;
    }
    const t2 = (l + h) / 2;
    const xOuter = rawHeartX(t2) * SCALE_X;
    if (xOuter <= 0.005) return [];
    return [[-xOuter, xOuter]];
  }
}

/**
 * Evaluates the signed distance field / implicit heart boundary function:
 * f(x, y) <= 0 inside the heart, > 0 outside, = 0 on the boundary.
 */
export function heartEquation(x: number, y: number): number {
  if (y > 1.22) return y - 1.22;
  if (y < -1.0) return -1.0 - y;
  const slices = getHeartSlicesAtY(y);
  const xa = Math.abs(x);
  if (slices.length === 0) {
    if (y <= -0.99) return xa + (-1.0 - y);
    return 0.5;
  }
  if (slices.length === 2) {
    const xInner = slices[1][0];
    const xOuter = slices[1][1];
    if (xa < xInner) return xInner - xa;
    if (xa > xOuter) return xa - xOuter;
    return -Math.min(xa - xInner, xOuter - xa);
  } else {
    const xOuter = slices[0][1];
    if (xa > xOuter) return xa - xOuter;
    return -(xOuter - xa);
  }
}

/**
 * Pseudo-random generator with seed support for deterministic arrangements
 */
export class SeededRNG {
  private state: number;

  constructor(seed = 123456789) {
    this.state = seed;
  }

  next(): number {
    this.state = (this.state * 1664525 + 1013904223) % 4294967296;
    return this.state / 4294967296;
  }

  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  choice<T>(items: T[]): T {
    return items[Math.floor(this.next() * items.length)];
  }
}

/**
 * Text measurement fallback when DOM canvas is not present (e.g. CLI/tests)
 */
export function estimateTextWidth(text: string, fontSize: number): number {
  let width = 0;
  for (const char of text) {
    const code = char.charCodeAt(0);
    // Non-latin / CJK / Indic scripts generally have larger width-to-height ratio
    if (code > 0x0900) {
      width += fontSize * 0.95;
    } else if (/[mwWM]/.test(char)) {
      width += fontSize * 0.8;
    } else if (/[ijlItf1]/.test(char)) {
      width += fontSize * 0.35;
    } else if (char === ' ') {
      width += fontSize * 0.35;
    } else {
      width += fontSize * 0.58;
    }
  }
  return width;
}

/**
 * Primary layout generator for the Typographic Heart
 */
export function calculateHeartLayout(
  allPhrases: HeartPhraseSource[],
  options: RenderOptions,
  measureFn?: (text: string, fontSize: number) => number
): HeartLayoutResult {
  const {
    width,
    height,
    loveRatio = 70,
    minFontSize = 11,
    maxFontSize = 22,
    textDensity = 'balanced',
    colorPalette = DEFAULT_PALETTE,
    highlightFrequency = 0.15,
    seed = Math.floor(Math.random() * 1000000),
  } = options;

  const rng = new SeededRNG(seed);
  const measure = measureFn || ((t, f) => estimateTextWidth(t, f));

  // Separate pools
  const lovePool = allPhrases.filter(p => p.type === 'love');
  const nickPool = allPhrases.filter(p => p.type === 'nickname');

  // Fallback defaults if empty pools provided
  const safeLovePool = lovePool.length > 0 ? lovePool : [{ id: 'l-def', text: 'I love you', type: 'love' as const }];
  const safeNickPool = nickPool.length > 0 ? nickPool : [{ id: 'n-def', text: 'sweetheart', type: 'nickname' as const }];

  // Responsive sizing box: preserve aspect ratio and fit gracefully within viewport
  // Maintain safe margins away from edge borders, top viewer controls (48px) and status bar (36px)
  const safeAvailW = Math.max(260, width - 40);
  const safeAvailH = Math.max(260, height - 90);
  const maxDimension = Math.min(safeAvailW, safeAvailH, 1300);
  const compositionScale = maxDimension / 2.30;

  const centerX = width / 2;
  const centerY = height / 2;

  // Determine row count and row height based on height and density
  let densityFactor = 1.0;
  if (textDensity === 'compact') densityFactor = 0.85;
  if (textDensity === 'spacious') densityFactor = 1.18;

  // Adapt font sizes responsively to viewport scale
  const responsiveScale = Math.min(1.0, Math.max(0.55, width / 1000));
  const effectiveMinFont = Math.max(7, Math.round(minFontSize * responsiveScale));
  const effectiveMaxFont = Math.max(effectiveMinFont + 2, Math.round(maxFontSize * responsiveScale));

  const baseRowHeight = Math.max(13, Math.round((effectiveMinFont + effectiveMaxFont) * 0.68 * densityFactor));
  const numRows = Math.max(16, Math.min(46, Math.floor((maxDimension * 0.92) / baseRowHeight)));

  const placedPhrases: PlacedPhrase[] = [];
  let loveCount = 0;
  let nicknameCount = 0;

  // Y range from top lobe peak (1.22) to bottom tip (-0.96)
  const yTop = 1.22;
  const yBottom = -0.96;

  let lastPlacedText = '';

  for (let r = 0; r < numRows; r++) {
    const tNorm = r / (numRows - 1);
    const cartesianY = yTop - tNorm * (yTop - yBottom);
    const pixelY = centerY - cartesianY * compositionScale;

    // Row font size variation: middle body is slightly larger, top/bottom slightly more refined
    const sizeProgress = 1 - Math.abs(cartesianY) * 0.45;
    const rowFontSize = Math.round(
      effectiveMinFont + (effectiveMaxFont - effectiveMinFont) * Math.max(0, Math.min(1, sizeProgress))
    );
    const rowHeight = rowFontSize + 4;

    const slices = getHeartSlicesAtY(cartesianY);

    for (const [xStartNorm, xEndNorm] of slices) {
      const segPixelLeft = centerX + xStartNorm * compositionScale;
      const segPixelRight = centerX + xEndNorm * compositionScale;
      const segWidth = segPixelRight - segPixelLeft;

      if (segWidth < rowFontSize * 1.5) {
        continue;
      }

      // Determine how many phrases to place horizontally in this segment
      // Fill the segment so words define the heart silhouette
      let currentX = segPixelLeft;
      const availableWidth = segWidth;
      let usedWidth = 0;
      const segmentPhrases: Array<{
        phrase: HeartPhraseSource;
        width: number;
        fontSize: number;
      }> = [];

      let attempts = 0;
      while (usedWidth < availableWidth * 0.95 && attempts < 12) {
        attempts++;

        // Determine whether this phrase should be love or nickname to strictly maintain ratio
        const totalSoFar = loveCount + nicknameCount;
        const currentLovePercentage = totalSoFar > 0 ? (loveCount / totalSoFar) * 100 : 0;
        const targetLovePercentage = loveRatio;

        const pickLove = totalSoFar === 0 ? true : currentLovePercentage < targetLovePercentage;
        const sourcePool = pickLove ? safeLovePool : safeNickPool;

        // Pick phrase from pool, preferring shorter phrases if remaining space is tight
        const remainingSpace = availableWidth - usedWidth;
        let candidate = rng.choice(sourcePool);
        if (remainingSpace < availableWidth * 0.4) {
          // Sort or filter shorter candidates
          const shortCandidates = sourcePool.filter(p => p.text.length <= 12);
          if (shortCandidates.length > 0) {
            candidate = rng.choice(shortCandidates);
          }
        }

        if (candidate.text === lastPlacedText && sourcePool.length > 1) {
          candidate = rng.choice(sourcePool);
        }

        // Slight font size variation (+/- 1px) for handcrafted organic character
        let phraseFontSize = Math.max(
          effectiveMinFont,
          Math.min(effectiveMaxFont, rowFontSize + Math.round(rng.range(-1, 1)))
        );
        let w = measure(candidate.text, phraseFontSize);

        if (w <= remainingSpace) {
          segmentPhrases.push({ phrase: candidate, width: w, fontSize: phraseFontSize });
          usedWidth += w;
          lastPlacedText = candidate.text;

          if (candidate.type === 'love') {
            loveCount++;
          } else {
            nicknameCount++;
          }
        } else if (segmentPhrases.length === 0) {
          // If first phrase doesn't fit, scale font down to fit within available width
          while (w > availableWidth && phraseFontSize > effectiveMinFont) {
            phraseFontSize--;
            w = measure(candidate.text, phraseFontSize);
          }
          if (w <= availableWidth) {
            segmentPhrases.push({ phrase: candidate, width: w, fontSize: phraseFontSize });
            usedWidth += w;
            lastPlacedText = candidate.text;
            if (candidate.type === 'love') loveCount++;
            else nicknameCount++;
          }
          break;
        } else {
          break;
        }
      }

      if (segmentPhrases.length === 0) continue;

      // Distribute whitespace evenly across segment, enforcing minimum spacing
      const minGap = Math.max(6, Math.round(rowFontSize * 0.35));
      const remainingWhitespace = Math.max(0, availableWidth - usedWidth);
      const gap = segmentPhrases.length > 1
        ? Math.max(minGap, remainingWhitespace / (segmentPhrases.length + 1))
        : remainingWhitespace / 2;

      currentX += gap;

      const segmentCount = segmentPhrases.length;
      for (let col = 0; col < segmentCount; col++) {
        const item = segmentPhrases[col];

        // Outline vs inner depth calculation:
        // 1. Column edge ratio: 0 at outer edges of this segment, 1 at center
        const colEdgeRatio = segmentCount <= 1
          ? 0.5
          : segmentCount === 2
            ? 0.25
            : Math.min(col, segmentCount - 1 - col) / ((segmentCount - 1) / 2 || 1);

        // 2. Row edge ratio: 0 at top lobe peaks or bottom tip, 1 at mid-height
        const rowEdgeRatio = Math.min(r, numRows - 1 - r) / ((numRows - 1) / 2 || 1);

        // 3. Mathematical heart equation contour distance:
        // Inside the heart, eqVal ranges from ~0 (perimeter) to ~0.35-0.45 (core)
        const phraseMidX = (currentX + item.width / 2 - centerX) / compositionScale;
        const phraseMidY = (centerY - (pixelY + rowHeight / 2)) / compositionScale;
        const eqVal = Math.abs(heartEquation(phraseMidX, phraseMidY));
        const mathDepth = Math.min(1, eqVal / 0.32);

        // Outline phrases (outer perimeter cols or top/bottom rows) have low depth near 0
        // Inner core phrases have high depth up to 1.0
        const isOutlinePhrase = col === 0 || col === segmentCount - 1 || r <= 1 || r >= numRows - 2;
        let depth: number;
        if (isOutlinePhrase) {
          depth = Math.min(0.18, mathDepth * 0.25);
        } else {
          depth = Math.min(1, Math.max(0.22, mathDepth * 0.65 + colEdgeRatio * 0.20 + rowEdgeRatio * 0.15));
        }

        // Phase delay: outer perimeter begins immediately (0 - 100ms), inner center finishes at ~1300ms
        const phaseDelay = Math.round(depth * 1300);

        // Color assignment:
        // Use darker rich reds for background mass, brighter reds sparingly for highlights
        const isHighlight = rng.next() < highlightFrequency;
        let color: string;
        if (isHighlight) {
          // Select from brighter shades (upper half of palette)
          const highlightShades = colorPalette.slice(Math.floor(colorPalette.length / 2));
          color = rng.choice(highlightShades) || '#FF334F';
        } else {
          // Select from deep subtle reds (lower half of palette)
          const deepShades = colorPalette.slice(0, Math.ceil(colorPalette.length / 2));
          color = rng.choice(deepShades) || '#800020';
        }

        placedPhrases.push({
          id: `${item.phrase.id}-${r}-${col}`,
          text: item.phrase.text,
          language: item.phrase.language,
          type: item.phrase.type,
          x: Math.round(currentX),
          y: Math.round(pixelY),
          width: Math.round(item.width),
          height: rowHeight,
          fontSize: item.fontSize,
          color,
          isHighlight,
          row: r,
          col,
          depth,
          phaseDelay,
        });

        currentX += item.width + gap;
      }
    }
  }

  // Calculate actual ratios
  const totalPhrases = loveCount + nicknameCount;
  const actualLoveRatio = totalPhrases > 0 ? Math.round((loveCount / totalPhrases) * 100) : 0;
  const actualNicknameRatio = totalPhrases > 0 ? 100 - actualLoveRatio : 0;

  // Compute bounding box
  let minX = width;
  let maxX = 0;
  let minY = height;
  let maxY = 0;

  for (const p of placedPhrases) {
    if (p.x < minX) minX = p.x;
    if (p.x + p.width > maxX) maxX = p.x + p.width;
    if (p.y < minY) minY = p.y;
    if (p.y + p.height > maxY) maxY = p.y + p.height;
  }

  // Exact mathematical centering: shift phrases so the bounding box is dead-center
  if (placedPhrases.length > 0 && minX < maxX && minY < maxY) {
    const heartWidth = maxX - minX;
    const heartHeight = maxY - minY;
    const shiftX = Math.round((width - heartWidth) / 2 - minX);
    const shiftY = Math.round((height - heartHeight) / 2 - minY);

    for (const p of placedPhrases) {
      p.x += shiftX;
      p.y += shiftY;
    }

    minX += shiftX;
    maxX += shiftX;
    minY += shiftY;
    maxY += shiftY;
  }

  return {
    phrases: placedPhrases,
    totalPhrases,
    loveCount,
    nicknameCount,
    actualLoveRatio,
    actualNicknameRatio,
    bounds: { minX, maxX, minY, maxY },
  };
}
